import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { Question, Client } from '../../types';
import './QuestionForm.css';

interface QuestionFormProps {
  question?: Question | null;
  clientId: number;
  onSuccess: () => void;
  onCancel: () => void;
}

const QuestionForm: React.FC<QuestionFormProps> = ({
  question,
  clientId,
  onSuccess,
  onCancel,
}) => {
  const { isAdmin } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [formData, setFormData] = useState({
    question_title: '',
    client_id: clientId,
    status: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (question) {
      setFormData({
        question_title: question.question_title,
        client_id: question.client_id,
        status: question.status,
      });
    }
    
    if (isAdmin()) {
      fetchClients();
    }
  }, [question, isAdmin]);

  const fetchClients = async () => {
    try {
      const response = await api.get<Client[]>('/clients');
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (question) {
        // Update existing question
        await api.put(`/questions/${question.id}`, {
          question_title: formData.question_title,
          status: formData.status,
        });
      } else {
        // Create new question
        await api.post('/questions', {
          question_title: formData.question_title,
          client_id: formData.client_id,
          status: formData.status,
        });
      }
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save question');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="question-form-container">
      <form onSubmit={handleSubmit} className="question-form">
        <h3>{question ? 'Edit Question' : 'Add New Question'}</h3>

        {error && <div className="alert alert-error">{error}</div>}

        {isAdmin() && (
          <div className="form-group">
            <label htmlFor="client_id">Company *</label>
            <select
              id="client_id"
              value={formData.client_id}
              onChange={(e) =>
                setFormData({ ...formData, client_id: parseInt(e.target.value, 10) })
              }
              required
              disabled={!!question} // Disable client selection when editing
            >
              <option value="">Select Company</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.client_name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="form-group">
          <label htmlFor="question_title">Question Title *</label>
          <input
            type="text"
            id="question_title"
            value={formData.question_title}
            onChange={(e) =>
              setFormData({ ...formData, question_title: e.target.value })
            }
            required
            maxLength={255}
            placeholder="Enter question title"
          />
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={formData.status}
              onChange={(e) =>
                setFormData({ ...formData, status: e.target.checked })
              }
            />
            <span>Active</span>
          </label>
        </div>

        <div className="form-actions">
          <button type="submit" disabled={loading} className="btn btn-primary">
            {loading ? 'Saving...' : question ? 'Update' : 'Create'}
          </button>
          <button type="button" onClick={onCancel} className="btn btn-secondary">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default QuestionForm;
